import React, { useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useProjectContext } from "../layout/ProjectContext";
import {
  useGetAlertProfilesQuery,
  useCreateAlertProfileMutation,
  useUpdateAlertProfileMutation,
  useDeleteAlertProfileMutation,
  useSetProfileDevicesMutation,
  type AlertProfile,
  type AlertProfileType,
  type AlertLevel,
  type VibrationAlertLevelJson,
} from "../features/alert-profiles/apiSlice";
import { useGetDevicesByProjectQuery } from "../features/devices/apiSlice";
import { useGetDistortionPairsQuery } from "../features/distortion-pairs/apiSlice";
import EmptyHint from "../components/EmptyHint";
import { isMovementType } from "../utils/movementTypes";

const PROFILE_TYPE_LABELS: Record<AlertProfileType, string> = {
  ground_settlement: "Ground Settlement",
  building_settlement: "Building Settlement",
  utility_settlement: "Utility Settlement",
  ground_water: "Ground Water",
  crack: "Crack Gauge",
  tilting: "Tilting",
  vibration: "Vibration",
  angular_distortion_building: "Angular Distortion (Building)",
  angular_distortion_utility: "Angular Distortion (Utility)",
  movement: "Movement",
  movement_settlement: "Movement (Settlement)",
  movement_lateral: "Movement (Lateral)",
};

// Ground water is managed per-device; hidden from this UI
const VISIBLE_PROFILE_TYPES: AlertProfileType[] = [
  "movement_settlement",
  "movement_lateral",
  "crack",
  "tilting",
  "vibration",
  "angular_distortion_building",
  "angular_distortion_utility",
];

// Tilting & Angular Distortion use slope ratio (1/N) display, stored as radian (1/N)
const GRADIENT_TYPES: AlertProfileType[] = [
  "tilting",
  "angular_distortion_building",
  "angular_distortion_utility",
];

const UNIT_LABELS: Partial<Record<AlertProfileType, string>> = {
  ground_settlement: "mm",
  building_settlement: "mm",
  utility_settlement: "mm",
  crack: "mm",
  ground_water: "mPD",
  vibration: "mm/s",
  movement: "mm",
  movement_settlement: "mm",
  movement_lateral: "mm",
};

const isGradientType = (t: AlertProfileType) => GRADIENT_TYPES.includes(t);

// Maps each profile type to the device_type(s) that are compatible with it
const PROFILE_TYPE_TO_DEVICE_TYPES: Record<AlertProfileType, string[]> = {
  ground_settlement: ["ground_settlement"],
  building_settlement: ["building_settlement"],
  utility_settlement: ["utility_settlement"],
  ground_water: ["ground_water", "piezometer", "standpipe"],
  crack: ["crack"],
  tilting: ["tilting"],
  vibration: ["vibration"],
  // Angular distortion profiles link to distortion pairs, not devices directly
  angular_distortion_building: [],
  angular_distortion_utility: [],
  movement: [
    "movement",
    "ground_movement",
    "building_movement",
    "utility_movement",
  ],
  movement_settlement: [
    "movement",
    "ground_movement",
    "building_movement",
    "utility_movement",
  ],
  movement_lateral: [
    "movement",
    "ground_movement",
    "building_movement",
    "utility_movement",
  ],
};

const ANGULAR_DISTORTION_TYPES: AlertProfileType[] = [
  "angular_distortion_building",
  "angular_distortion_utility",
];

// Filter predicate for angular distortion pair compatibility:
// Building: both points must be building_settlement
// Utility: any other combination (at least one point is NOT building_settlement)
function isCompatibleAngularPair(
  pair: { pointAType?: string; pointBType?: string; isActive: boolean },
  profileType: AlertProfileType,
): boolean {
  if (!pair.isActive) return false;
  if (isMovementType(pair.pointAType) && isMovementType(pair.pointBType)) {
    return ANGULAR_DISTORTION_TYPES.includes(profileType);
  }
  const bothBuilding =
    pair.pointAType === "building_settlement" &&
    pair.pointBType === "building_settlement";
  if (profileType === "angular_distortion_building") return bothBuilding;
  if (profileType === "angular_distortion_utility") return !bothBuilding;
  return false;
}

function defaultLevels(): AlertLevel[] {
  return [
    { order: 1, name: "Alert", value: 0, entry_mode: "value" },
    { order: 2, name: "Alarm", value: 0, entry_mode: "value" },
    { order: 3, name: "Action1", value: 0, entry_mode: "value" },
    { order: 4, name: "Action2", value: 0, entry_mode: "value" },
    { order: 5, name: "Action3", value: 0, entry_mode: "value" },
  ];
}

function flatLevelsFromJson(
  alertLevelJson: AlertLevel[] | VibrationAlertLevelJson,
): AlertLevel[] {
  if (Array.isArray(alertLevelJson)) return alertLevelJson;
  return alertLevelJson.tran_rs ?? defaultLevels();
}

interface DraftState {
  id: number | null;
  description: string;
  profile_type: AlertProfileType;
  levels: AlertLevel[];
  device_ids: string[];
}

const AlertProfiles: React.FC = () => {
  const { projectId } = useProjectContext();
  const { data: profiles, isFetching } = useGetAlertProfilesQuery(
    projectId ?? undefined,
    { skip: projectId == null, refetchOnMountOrArgChange: true },
  );
  const [createProfile] = useCreateAlertProfileMutation();
  const [updateProfile] = useUpdateAlertProfileMutation();
  const [deleteProfile] = useDeleteAlertProfileMutation();
  const [setProfileDevices] = useSetProfileDevicesMutation();

  const { data: devices } = useGetDevicesByProjectQuery(projectId as number, {
    skip: projectId == null,
    refetchOnMountOrArgChange: true,
  });

  const { data: distortionPairs } = useGetDistortionPairsQuery(
    projectId as number,
    { skip: projectId == null, refetchOnMountOrArgChange: true },
  );

  const [draft, setDraft] = useState<DraftState | null>(null);
  const [typeFilter, setTypeFilter] = useState<string>("All types");

  // Exclude ground_water from list & filter
  const filtered = useMemo(
    () =>
      (profiles ?? []).filter(
        (p) =>
          p.profile_type !== "ground_water" &&
          VISIBLE_PROFILE_TYPES.includes(p.profile_type) &&
          (typeFilter === "All types" ||
            PROFILE_TYPE_LABELS[p.profile_type] === typeFilter),
      ),
    [profiles, typeFilter],
  );

  const openCreate = () =>
    setDraft({
      id: null,
      description: "",
      profile_type: "movement_settlement",
      levels: defaultLevels(),
      device_ids: [],
    });

  const openEdit = (p: AlertProfile) => {
    const gradient = isGradientType(p.profile_type);

    const levels = flatLevelsFromJson(p.alert_level_json).map((l) => ({
      ...l,
      // Gradient stored as radian (1/N); display N to user
      value: gradient && l.value ? 1 / l.value : l.value,
      entry_mode: (gradient ? "gradient" : "value") as "value" | "gradient",
    }));
    setDraft({
      id: p.id,
      description: p.description ?? "",
      profile_type: p.profile_type,
      levels,
      device_ids: p.devices.map((d) => d.id),
    });
  };

  const handleDelete = async (p: AlertProfile) => {
    if (p.profile_type === "vibration") {
      void Swal.fire({
        icon: "info",
        title: "Default vibration profiles cannot be deleted",
      });
      return;
    }

    const res = await Swal.fire({
      icon: "warning",
      title: "Delete profile?",
      text: `"${p.description}" will be removed.`,
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#dc2626",
    });
    if (!res.isConfirmed) return;
    try {
      await deleteProfile(p.id).unwrap();
    } catch {
      Swal.fire({ icon: "error", title: "Delete failed" });
    }
  };

  const saveDraft = async () => {
    if (!draft || projectId == null) return;

    const gradient = isGradientType(draft.profile_type);

    const alertLevelJson = draft.levels.map((l) => ({
      ...l,
      // Convert display N back to radian (1/N) for gradient types
      value: gradient && l.value ? 1 / l.value : l.value,
      entry_mode: gradient ? ("gradient" as const) : ("value" as const),
    }));

    const body = {
      project_id: projectId,
      description: draft.description,
      profile_type: draft.profile_type,
      alert_level_json: alertLevelJson,
    };

    try {
      let profileId: number;
      if (draft.id == null) {
        const created = await createProfile(body).unwrap();
        profileId = created.id;
      } else {
        await updateProfile({ id: draft.id, body }).unwrap();
        profileId = draft.id;
      }
      await setProfileDevices({ id: profileId, device_ids: draft.device_ids });
      setDraft(null);
    } catch {
      Swal.fire({ icon: "error", title: "Save failed" });
    }
  };

  const updateLevel = (idx: number, value: number) =>
    setDraft((d) =>
      d
        ? {
            ...d,
            levels: d.levels.map((l, i) => (i === idx ? { ...l, value } : l)),
          }
        : d,
    );

  const handleProfileTypeChange = (type: AlertProfileType) => {
    if (!draft) return;
    const gradient = isGradientType(type);
    setDraft({
      ...draft,
      profile_type: type,
      levels: defaultLevels().map((l) => ({
        ...l,
        entry_mode: gradient ? ("gradient" as const) : ("value" as const),
      })),
      // Reset selections — the new type may have a different compatible device set
      device_ids: [],
    });
  };

  const toggleDevice = (id: string) => {
    if (!draft) return;
    setDraft((d) =>
      d
        ? {
            ...d,
            device_ids: d.device_ids.includes(id)
              ? d.device_ids.filter((x) => x !== id)
              : [...d.device_ids, id],
          }
        : d,
    );
  };

  if (projectId == null) {
    return (
      <EmptyHint
        icon="bi-folder2-open"
        title="Select a project"
        subtitle="Choose a project from the top bar to manage alert profiles."
      />
    );
  }

  const draftIsGradient = draft ? isGradientType(draft.profile_type) : false;
  const draftUnit = draft ? UNIT_LABELS[draft.profile_type] : undefined;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Action Profiles</h1>
          <div className="sub">
            {filtered.length} profile{filtered.length === 1 ? "" : "s"}
          </div>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>
          <i className="bi bi-plus-lg" /> New profile
        </button>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Profiles</h2>
          <select
            className="select"
            style={{ width: 220 }}
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option>All types</option>
            {VISIBLE_PROFILE_TYPES.map((t) => (
              <option key={t}>{PROFILE_TYPE_LABELS[t]}</option>
            ))}
          </select>
        </div>
        {isFetching ? (
          <EmptyHint
            icon="bi-hourglass-split"
            title="Loading…"
            subtitle="Fetching alert profiles."
          />
        ) : filtered.length === 0 ? (
          <EmptyHint
            icon="bi-shield-exclamation"
            title="No alert profiles"
            subtitle="Create a profile to define alert/alarm/action thresholds."
          />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Type</th>
                <th>Thresholds</th>
                <th>Checkpoints</th>
                <th style={{ width: 120 }} />
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const gradient = isGradientType(p.profile_type);
                const pUnit = UNIT_LABELS[p.profile_type];
                // Filter out deleted devices/pairs
                const isAngular = ANGULAR_DISTORTION_TYPES.includes(
                  p.profile_type,
                );
                const existingDevices = p.devices.filter((d) => {
                  if (isAngular) {
                    return (distortionPairs ?? []).some(
                      (pair) => pair.id === d.id,
                    );
                  }
                  return (devices ?? []).some((dev) => dev.id === d.id);
                });
                return (
                  <tr key={p.id}>
                    <td className="fw-600">{p.description}</td>
                    <td>{PROFILE_TYPE_LABELS[p.profile_type]}</td>
                    <td>
                      <div className="row gap-2" style={{ flexWrap: "wrap" }}>
                        {flatLevelsFromJson(p.alert_level_json).map((l) => (
                          <span key={l.order} className="badge">
                            {l.name}:{" "}
                            {gradient && l.value
                              ? `1/${Math.round(1 / l.value)}`
                              : `${l.value}${pUnit ? ` ${pUnit}` : ""}`}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      {existingDevices.length === 0 ? (
                        <span className="text-muted text-sm">None</span>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "4px",
                          }}
                        >
                          {existingDevices.map((d) => (
                            <span key={d.id} className="badge ok">
                              {d.device_name}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="row gap-2">
                        <button
                          className="btn btn-sm"
                          onClick={() => openEdit(p)}
                        >
                          <i className="bi bi-pencil" />
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(p)}
                          disabled={p.profile_type === "vibration"}
                          title={
                            p.profile_type === "vibration"
                              ? "Default vibration profiles cannot be deleted"
                              : "Delete"
                          }
                        >
                          <i className="bi bi-trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Editor modal */}
      <style>{`
        .device-pick { display:flex; flex-wrap:wrap; gap:8px; max-height:220px; overflow:auto; }
        .device-pick .chip { cursor:pointer; user-select:none; }
        .device-pick .chip.on { background: var(--c-primary); color:#fff; border-color: var(--c-primary); }
      `}</style>
      <div className={`modal-backdrop ${draft ? "show" : ""}`}>
        {draft && (
          <div className="modal">
            <div className="modal-head">
              <h3>{draft.id == null ? "New profile" : "Edit profile"}</h3>
              <button
                className="btn btn-ghost btn-icon"
                onClick={() => setDraft(null)}
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>
            <div
              className="modal-body"
              style={{ maxHeight: "65vh", overflowY: "auto" }}
            >
              <div className="field mb-3">
                <label>Description</label>
                <input
                  className="input"
                  value={draft.description}
                  disabled={draft.profile_type === "vibration"}
                  onChange={(e) =>
                    setDraft({ ...draft, description: e.target.value })
                  }
                  placeholder="e.g. Default movement"
                />
              </div>
              <div className="field mb-3">
                <label>Profile type</label>
                <select
                  className="select"
                  value={draft.profile_type}
                  disabled={draft.id != null}
                  onChange={(e) =>
                    handleProfileTypeChange(e.target.value as AlertProfileType)
                  }
                >
                  {VISIBLE_PROFILE_TYPES.filter(
                    (t) => draft.id != null || t !== "vibration",
                  ).map((t) => (
                    <option key={t} value={t}>
                      {PROFILE_TYPE_LABELS[t]}
                    </option>
                  ))}
                </select>
              </div>

              {/* Threshold inputs */}
              {draft.profile_type !== "ground_water" && (
                <>
                  <div className="fw-600 mb-2">
                    Thresholds
                    {draftIsGradient ? (
                      <span
                        style={{
                          fontWeight: 400,
                          marginLeft: 8,
                          fontSize: "0.85em",
                          color: "var(--text-muted, #888)",
                        }}
                      >
                        Slope ratio — enter N (stored as 1/N rad)
                      </span>
                    ) : draftUnit ? (
                      <span
                        style={{
                          fontWeight: 400,
                          marginLeft: 8,
                          fontSize: "0.85em",
                          color: "var(--text-muted, #888)",
                        }}
                      >
                        Unit: {draftUnit}
                      </span>
                    ) : null}
                  </div>
                  {draft.levels.map((l, i) => (
                    <div className="field mb-2" key={l.order}>
                      <label>
                        {l.name}
                        {draftIsGradient
                          ? " — N (denominator of 1/N)"
                          : draftUnit
                            ? ` (${draftUnit})`
                            : ""}
                      </label>
                      <input
                        className="input"
                        type="number"
                        step="any"
                        value={l.value}
                        onChange={(e) => updateLevel(i, Number(e.target.value))}
                      />
                      {draftIsGradient && l.value > 0 && (
                        <small style={{ color: "var(--text-muted, #888)" }}>
                          = 1/{Math.round(l.value)} · stored as{" "}
                          {(1 / l.value).toFixed(6)} rad
                        </small>
                      )}
                    </div>
                  ))}
                </>
              )}

              {/* Checkpoint picker — only shows devices whose type matches the profile */}
              {(() => {
                const isAngular = ANGULAR_DISTORTION_TYPES.includes(
                  draft.profile_type,
                );

                if (isAngular) {
                  const compatible = (distortionPairs ?? []).filter((pair) =>
                    isCompatibleAngularPair(pair, draft.profile_type),
                  );
                  return (
                    <div className="field mb-3">
                      <label>
                        Linked Distortion Pairs ({draft.device_ids.length}{" "}
                        selected)
                      </label>
                      <div className="device-pick">
                        {compatible.length === 0 ? (
                          <span className="text-muted text-sm">
                            No compatible distortion pairs for this project.
                          </span>
                        ) : (
                          compatible.map((pair) => (
                            <span
                              key={pair.id}
                              className={`badge muted chip ${
                                draft.device_ids.includes(pair.id) ? "on" : ""
                              }`}
                              onClick={() => toggleDevice(pair.id)}
                            >
                              {pair.pairLabel ?? pair.id}
                            </span>
                          ))
                        )}
                      </div>
                    </div>
                  );
                }

                const allowed =
                  PROFILE_TYPE_TO_DEVICE_TYPES[draft.profile_type] ?? [];
                const compatible = (devices ?? []).filter((d) =>
                  allowed.includes(d.device_type),
                );
                return (
                  <div className="field mb-3">
                    <label>
                      Linked Checkpoints ({draft.device_ids.length} selected)
                    </label>
                    {(draft.profile_type === "movement_settlement" ||
                      draft.profile_type === "movement_lateral") && (
                      <div
                        className="text-muted text-sm"
                        style={{ marginBottom: 6 }}
                      >
                        A movement checkpoint takes two profiles:{" "}
                        <b>Movement (Settlement)</b> for vertical settlement and{" "}
                        <b>Movement (Lateral)</b> for lateral movement. This
                        profile applies to the{" "}
                        <b>
                          {draft.profile_type === "movement_settlement"
                            ? "settlement"
                            : "lateral"}
                        </b>{" "}
                        axis of the selected checkpoints.
                      </div>
                    )}
                    <div className="device-pick">
                      {compatible.length === 0 ? (
                        <span className="text-muted text-sm">
                          No compatible checkpoints for this profile type.
                        </span>
                      ) : (
                        compatible.map((d) => (
                          <span
                            key={d.id}
                            className={`badge muted chip ${
                              draft.device_ids.includes(d.id) ? "on" : ""
                            }`}
                            onClick={() => toggleDevice(d.id)}
                          >
                            {d.device_name}
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="modal-foot">
              <button className="btn" onClick={() => setDraft(null)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={saveDraft}
                disabled={!draft.description.trim()}
              >
                {draft.id == null ? "Create" : "Save"}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AlertProfiles;
